if (!customElements.get('tabbed-content')) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      // AS PDP Change 102 follow-up: tabList is the PDP scroller.
      this.tabScroller = this.tabList.matches('.tablist__scroller')
        ? this.tabList
        : this.tabList.querySelector('.tablist__scroller');

      // If no tab is active by default, activate the first tab.
      if (!this.activeTab) {
        this.activeTab = this.tabs[0];
        this.activateTab(this.activeTab);
      }

      this.addListeners();

      // AS PDP Change 102: expose horizontal tab overflow state for the cue.
      if (this.tabScroller) {
        this.updateTabOverflowCue = this.updateTabOverflowCue.bind(this);
        this.tabScroller.addEventListener('scroll', this.updateTabOverflowCue, { passive: true });

        // AS PDP Change 103: create one actionable PDP overflow cue.
        // AS PDP Change 103 follow-up: PDP ancestry exists at construction time.
        if (this.closest('.cc-product-details')) {
          this.tabOverflowCue = document.createElement('button');
          this.tabOverflowCue.type = 'button';
          this.tabOverflowCue.className = 'as-tablist-overflow-cue';
          this.tabOverflowCue.setAttribute('aria-label', 'Show more product information tabs');
          this.tabOverflowCue.textContent = '›';
          this.tabOverflowCue.hidden = true;
          this.handleTabOverflowCueClick = this.handleTabOverflowCueClick.bind(this);
          this.tabOverflowCue.addEventListener('click', this.handleTabOverflowCueClick);
          this.tabList.parentElement.appendChild(this.tabOverflowCue);

          // AS PDP Change 104: matching actionable left cue.
          this.tabOverflowBackCue = document.createElement('button');
          this.tabOverflowBackCue.type = 'button';
          this.tabOverflowBackCue.className = 'as-tablist-overflow-cue as-tablist-overflow-cue--back';
          this.tabOverflowBackCue.setAttribute('aria-label', 'Show previous product information tabs');
          this.tabOverflowBackCue.textContent = '‹';
          this.tabOverflowBackCue.hidden = true;
          this.handleTabOverflowBackCueClick = this.handleTabOverflowBackCueClick.bind(this);
          this.tabOverflowBackCue.addEventListener('click', this.handleTabOverflowBackCueClick);
          this.tabList.parentElement.insertBefore(this.tabOverflowBackCue, this.tabOverflowCue);
        }

        if ('ResizeObserver' in window) {
          this.tabResizeObserver = new ResizeObserver(this.updateTabOverflowCue);
          this.tabResizeObserver.observe(this.tabScroller);
        }

        requestAnimationFrame(this.updateTabOverflowCue);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.updateTabOverflowCue);
        }
      }
    }

    disconnectedCallback() {
      if (this.tabScroller && this.updateTabOverflowCue) {
        this.tabScroller.removeEventListener('scroll', this.updateTabOverflowCue);
      }
      if (this.tabResizeObserver) this.tabResizeObserver.disconnect();
      if (this.tabOverflowCue && this.handleTabOverflowCueClick) {
        this.tabOverflowCue.removeEventListener('click', this.handleTabOverflowCueClick);
      }
      if (this.tabOverflowBackCue && this.handleTabOverflowBackCueClick) {
        this.tabOverflowBackCue.removeEventListener('click', this.handleTabOverflowBackCueClick);
      }
    }

    addListeners() {
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    updateTabOverflowCue() {
      const isDesktop = window.matchMedia('(min-width: 769px)').matches;
      const hasOverflow = isDesktop
        && this.tabScroller.scrollWidth > this.tabScroller.clientWidth + 1;
      const remainingScroll = this.tabScroller.scrollWidth
        - this.tabScroller.clientWidth
        - this.tabScroller.scrollLeft;
      const atStart = !hasOverflow || this.tabScroller.scrollLeft <= 2;
      const atEnd = !hasOverflow || remainingScroll <= 2;

      this.classList.toggle('as-tablist-overflowing', hasOverflow);
      this.classList.toggle('as-tablist-at-start', atStart);
      this.classList.toggle('as-tablist-at-end', atEnd);
      if (this.tabOverflowCue) this.tabOverflowCue.hidden = !hasOverflow || atEnd;
      if (this.tabOverflowBackCue) this.tabOverflowBackCue.hidden = !hasOverflow || atStart;
    }

    handleTabOverflowCueClick() {
      this.tabScroller.scrollLeft = this.tabScroller.scrollWidth;
      this.updateTabOverflowCue();
    }

    handleTabOverflowBackCueClick() {
      this.tabScroller.scrollLeft = 0;
      this.updateTabOverflowCue();
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      this.activateTab(tab);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        // Activate the first tab.
        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        // Activate the last tab.
        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        // If active tab is the last tab.
        if (this.activeTab === this.tabs[this.tabs.length - 1]) {
          // Activate first tab.
          this.activateTab(this.tabs[0]);
        } else {
          // Activate next tab.
          this.activateTab(this.activeTab.nextElementSibling);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        // If active tab is the first tab.
        if (this.activeTab === this.tabs[0]) {
          this.activateTab(this.tabs[this.tabs.length - 1]);
        } else {
          // Activate the previous tab.
          this.activateTab(this.activeTab.previousElementSibling);
        }
      }
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     */
    activateTab(tab) {
      this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;

      if (document.activeElement.matches('.tablist__tab')) {
        tab.focus();
      }
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabState(tab, active) {
      tab.setAttribute('aria-selected', active);

      const panelId = tab.getAttribute('aria-controls');
      document.getElementById(panelId).hidden = !active;
    }
  }

  customElements.define('tabbed-content', Tabs);
}
